import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { notification } from 'antd';

import RemoteAccessAuditConfigurationContext from '@totem/components/services/remoteAccess/auditConfiguration/RemoteAccessAuditConfigurationContext';
import {
  OrganizationConfiguration,
  RequestRemoteAccessBuildingConfigurationUpdate,
  RequestRemoteAccessOrganizationConfigurationUpdate,
} from '@totem/components/services/remoteAccess/auditConfiguration/types';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import { securityGroupCheckConstraintEmpty } from '@totem/components/userContext/security/userUtilities';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { REMOTE_ACCESS_ENDPOINT } from '@totem/utilities/endpoints';

import '../remoteAccess.css';

type Props = {
  children?: ReactNode;
};

const RemoteAccessAuditConfigurationContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [refreshData, setRefreshData] = useState<boolean>(true);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [config, setConfig] = useState<OrganizationConfiguration>(null);

  const canView = useMemo(
    () =>
      permissions.remoteAccessAdmin.hasPermissions(
        userProfile,
        securityGroupCheckConstraintEmpty,
      ),
    [userProfile],
  );

  useEffect(() => {
    if (!canView) {
      setConfig(null);

      setLoading(false);
    }
  }, [canView]);

  useEffect(() => {
    if (!refreshData || !canView) {
      return;
    }
    if (isSending) {
      return;
    }

    setRefreshData(false);

    setIsSending(true);

    setLoading(true);

    fetch(`${REMOTE_ACCESS_ENDPOINT}/audit/configuration`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          throw new Error('Failed to load audit configuration.');
        }
        return res.json();
      })
      .then((result: OrganizationConfiguration) => {
        setConfig(result);
      })
      .catch(() => {
        notification.error({
          title: 'Error loading audit configuration',
          description: 'An error occurred while loading audit configuration.',
          duration: 0,
        });
      })
      .finally(() => {
        setLoading(false);

        setIsSending(false);
      });
  }, [refreshData, canView, isSending, getApiToken]);

  const handleRefresh = () => {
    setRefreshData(true);
  };

  const updateOrganization = async (
    payload: RequestRemoteAccessOrganizationConfigurationUpdate,
  ) => {
    if (isSending) {
      return false;
    }

    setIsSending(true);

    return fetch(`${REMOTE_ACCESS_ENDPOINT}/audit/configuration`, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (res.status < 400) {
          setRefreshData(true);
          return true;
        }
        notification.error({
          title: 'Error saving organization configuration',
          description:
            'An error occurred while updating organization settings.',
          duration: 0,
        });
        return false;
      })
      .catch(() => {
        notification.error({
          title: 'Error saving organization configuration',
          description:
            'An error occurred while updating organization settings.',
          duration: 0,
        });
        return false;
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const updateBuilding = async (
    payload: RequestRemoteAccessBuildingConfigurationUpdate,
  ) => {
    if (isSending) {
      return false;
    }

    setIsSending(true);

    return fetch(
      `${REMOTE_ACCESS_ENDPOINT}/audit/configuration/${payload.building.id}`,
      {
        method: 'POST',
        headers: new Headers({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      },
    )
      .then((res) => {
        if (res.status < 400) {
          setRefreshData(true);
          return true;
        }
        notification.error({
          title: 'Error saving building configuration',
          description: 'An error occurred while updating building settings.',
          duration: 0,
        });
        return false;
      })
      .catch(() => {
        notification.error({
          title: 'Error saving building configuration',
          description: 'An error occurred while updating building settings.',
          duration: 0,
        });
        return false;
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  return (
    <RemoteAccessAuditConfigurationContext.Provider
      value={{
        config,
        loading,
        canView,
        isSending,
        onRefresh: handleRefresh,
        onUpdateOrganization: updateOrganization,
        onUpdateBuilding: updateBuilding,
      }}
    >
      <div>{children}</div>
    </RemoteAccessAuditConfigurationContext.Provider>
  );
};

export default RemoteAccessAuditConfigurationContainer;
